// Saved Views — DealCloud-style shareable saved tables. See VIEWS_DESIGN.md.
//
//   • ViewsView      — one opened view (rendered as an editable table via the
//                      shared WidgetRenderer — inline cell edits PATCH through
//                      the existing entity endpoints, exactly like analytics
//                      list widgets), or the ViewEditor. The old gallery moved
//                      into the Analytics library (analytics.jsx, PR #151).
//   • SaveViewDialog — capture the current grid state (source/columns/filters/
//                      sort) as a new view. Used by the Companies grid.
//   • ShareViewDialog— change a view's visibility / explicit shares (with
//                      per-person can_edit grants). Also used by the Analytics
//                      library's view cards.
//
// Exposed on window (esbuild IIFE; cross-file via window.*).

const VIS_HUE = { private: 'slate', team: 'teal', users: 'violet', org: 'green' };
const VIS_OPTS = [
  ['private', 'Private', 'Only you'],
  ['team', 'My team', 'Your team (read-only)'],
  ['org', 'Everyone', 'Everyone in the org (read-only)'],
  ['users', 'Specific people', 'Pick people — optionally let them edit'],
];
const ENTITY_ICON = { companies: 'Building', contacts: 'User', engagements: 'Pipeline' };

const VisibilityPicker = ({ value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {VIS_OPTS.map(([v, label, sub]) => (
      <button key={v} onClick={() => onChange(v)} style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', textAlign: 'left', width: '100%',
        border: '1px solid ' + (value === v ? 'var(--accent)' : 'var(--border)'),
        background: value === v ? 'var(--accent-soft)' : 'var(--surface)',
        borderRadius: 8, cursor: 'pointer',
      }}>
        <window.Pill hue={VIS_HUE[v]} sz="sm">{label}</window.Pill>
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{sub}</span>
      </button>
    ))}
  </div>
);

// `editors`/`onToggleEdit` (optional) add a per-person "can edit" grant
// checkbox for the people already selected — team/org sharing stays read-only.
const MemberChecklist = ({ members, selected, onToggle, editors, onToggleEdit }) => (
  <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
    {members.map((m) => (
      <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, minWidth: 0 }}>
          <input type="checkbox" checked={selected.has(m.id)} onChange={() => onToggle(m.id)} />
          <span style={{ color: 'var(--text-2)' }}>{m.full_name}</span>
          <span style={{ color: 'var(--text-4)', fontSize: 11 }}>{(m.role || '').replace(/_/g, ' ')}</span>
        </label>
        {onToggleEdit && selected.has(m.id) && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'var(--text-3)', cursor: 'pointer', flexShrink: 0 }}>
            <input type="checkbox" checked={!!(editors && editors.has(m.id))} onChange={() => onToggleEdit(m.id)} />
            can edit
          </label>
        )}
      </div>
    ))}
    {!members.length && <div style={{ padding: 10, fontSize: 11.5, color: 'var(--text-4)' }}>Loading teammates…</div>}
  </div>
);


// ── Folders ───────────────────────────────────────────────────────────────────
// Views mirror dashboard groups: group_name is a nullable label, a folder
// exists while any view names it. '' = Ungrouped; '__new__' = typing a new one.
const FolderPicker = ({ value, onChange, groups }) => {
  const isNew = value === '__new__' || (!!value && !groups.includes(value));
  const opts = [
    { value: '', label: 'No folder' },
    ...groups.map((g) => ({ value: g, label: g })),
    { value: '__new__', label: '+ New folder…' },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, minWidth: 0 }}>
      <window.Combobox value={isNew ? '__new__' : (value || '')} onChange={(e) => onChange(e.target.value)}
        options={opts} style={{ flex: 1, minWidth: 120 }} />
      {isNew && (
        <window.TextInput value={value === '__new__' ? '' : value} placeholder="Folder name" autoFocus
          onChange={(e) => onChange(e.target.value || '__new__')} style={{ flex: 1, minWidth: 110 }} />
      )}
    </div>
  );
};
const cleanFolder = (v) => (v === '__new__' ? '' : String(v || '')).trim() || null;


// ── Save (capture current grid → view) ───────────────────────────────────────
const SaveViewDialog = ({ open, onClose, capture, defaultName, onSaved }) => {
  const [name, setName] = React.useState(defaultName || '');
  const [description, setDescription] = React.useState('');
  const [visibility, setVisibility] = React.useState('private');
  const [group, setGroup] = React.useState('');
  const [groups, setGroups] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setName(defaultName || ''); setDescription(''); setVisibility('private'); setGroup(''); setBusy(false);
      window.AtlasApi.views.list()
        .then((v) => setGroups([...new Set((v || []).map((x) => x.group_name).filter(Boolean))].sort()))
        .catch(() => setGroups([]));
    }
  }, [open, defaultName]);
  if (!open) return null;

  const save = async () => {
    const payload = capture && capture();
    if (!payload || !payload.spec || !(payload.spec.columns || []).length) {
      window.pushToast({ type: 'error', title: 'Nothing to save', body: 'This view has no columns.' });
      return;
    }
    setBusy(true);
    try {
      const v = await window.AtlasApi.views.create({
        name: name.trim() || 'Untitled view',
        description: description.trim() || null,
        visibility,
        group_name: cleanFolder(group),
        source_entity: payload.source_entity,
        spec: payload.spec,
      });
      window.pushToast({ type: 'success', title: 'View saved', body: v.name });
      onClose();
      if (onSaved) onSaved(v);
    } catch (e) {
      window.pushToast({ type: 'error', title: "Couldn't save view", body: (e && e.message) || String(e) });
    } finally { setBusy(false); }
  };

  return (
    <window.Drawer open onClose={onClose} title="Save as view" width={440}
      footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
        <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" onClick={save} disabled={busy || !name.trim()}>Save view</window.Btn>
      </div>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <window.Field label="Name">
          <window.TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Active food-mfg targets" autoFocus />
        </window.Field>
        <window.Field label="Description" hint="Optional">
          <window.TextInput value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's in this view?" />
        </window.Field>
        <window.Field label="Folder" hint="Optional">
          <FolderPicker value={group} onChange={setGroup} groups={groups} />
        </window.Field>
        <window.Field label="Who can see it">
          <VisibilityPicker value={visibility} onChange={setVisibility} />
        </window.Field>
      </div>
    </window.Drawer>
  );
};


// ── Share an existing view ────────────────────────────────────────────────────
const ShareViewDialog = ({ view, onClose, onSaved }) => {
  const [visibility, setVisibility] = React.useState(view.visibility);
  const [members, setMembers] = React.useState([]);
  const [selected, setSelected] = React.useState(() => new Set([
    ...(view.shared_user_ids || []),
    ...((view.shares || []).map((s) => s.user_id)),
  ]));
  // Per-user can_edit grants (only meaningful when visibility='users').
  const [editors, setEditors] = React.useState(() => new Set(
    (view.shares || []).filter((s) => s.can_edit).map((s) => s.user_id)
  ));
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (visibility === 'users' && !members.length) {
      window.AtlasApi.listTeamMembers().then((m) => setMembers(Array.isArray(m) ? m : [])).catch(() => {});
    }
  }, [visibility]);   // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleEdit = (id) => setEditors((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    setBusy(true);
    try {
      const ids = visibility === 'users' ? [...selected] : [];
      const updated = await window.AtlasApi.views.setShares(view.id, {
        visibility,
        shared_user_ids: ids,   // legacy mirror (view-only) for back-compat
        entries: ids.map((id) => ({ user_id: id, can_edit: editors.has(id) })),
      });
      window.pushToast({ type: 'success', title: 'Sharing updated' });
      onClose();
      if (onSaved) onSaved(updated);
    } catch (e) {
      window.pushToast({ type: 'error', title: "Couldn't update sharing", body: (e && e.message) || String(e) });
    } finally { setBusy(false); }
  };

  return (
    <window.Drawer open onClose={onClose} title="Share view" subtitle={view.name} width={440}
      footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
        <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" onClick={save} disabled={busy}>Save sharing</window.Btn>
      </div>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <window.Field label="Who can see it"><VisibilityPicker value={visibility} onChange={setVisibility} /></window.Field>
        {visibility === 'users' && (
          <window.Field label="People" hint="Checked people can view; “can edit” lets them change the view too">
            <MemberChecklist members={members} selected={selected} onToggle={toggle} editors={editors} onToggleEdit={toggleEdit} />
          </window.Field>
        )}
      </div>
    </window.Drawer>
  );
};


// ── One opened view (editable table / clickable chart) ───────────────────────
const SingleView = ({ viewId, back, activeUser, onEdit }) => {
  const [detail, setDetail] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [shareOpen, setShareOpen] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [pinOpen, setPinOpen] = React.useState(false);      // "Add to dashboard"
  const [drill, setDrill] = React.useState(null);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [filters, setFilters] = React.useState([]);       // UI rows {column, op, value, value2}
  const [applied, setApplied] = React.useState([]);        // last coerced filters sent to /run
  const [srcInfo, setSrcInfo] = React.useState(null);      // the view's source (columns for pickers)
  const reqSeq = React.useRef(0);

  const spec = (detail && detail.spec) || {};
  // Structured (manual) views recompile with ad-hoc filters pushed into the
  // SQL; AI free-SQL views push declared `filterable` keys down at their
  // /*FILTERS*/ token and post-filter the rest server-side.
  const qsource = spec.query && spec.query.source;
  const filterable = (!qsource && Array.isArray(spec.filterable) && spec.filterable.length) ? spec.filterable : null;

  const run = React.useCallback(async (refresh, fl) => {
    const seq = ++reqSeq.current;
    setErr('');
    try {
      const res = await window.AtlasApi.views.run(viewId, refresh, fl || []);
      if (seq === reqSeq.current) setData(res);
    } catch (e) {
      if (seq === reqSeq.current) setErr((e && e.message) || String(e));
    }
  }, [viewId]);
  const loadDetail = React.useCallback(() => {
    window.AtlasApi.views.get(viewId).then((d) => {
      setDetail(d);
      // Saved filter defaults (owner-set) seed the bar + the first run.
      const df = (d.spec && d.spec.default_filters) || [];
      if (df.length) { setFilters(filtersToRows(df)); setApplied(df); run(false, df); }
      else run(false);
    }).catch((e) => setErr((e && e.message) || String(e)));
  }, [viewId, run]);
  React.useEffect(() => { loadDetail(); }, [viewId]);  // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!qsource) return;
    window.AtlasApi.views.sources()
      .then((all) => setSrcInfo((Array.isArray(all) ? all : []).find((s) => s.name === qsource) || null))
      .catch(() => {});
  }, [qsource]);

  // Filterable columns: real + lookup columns for structured views; the
  // spec-declared `filterable` keys for AI views that carry them (pushed into
  // the SQL pre-aggregation); otherwise the RESULT columns with categories
  // inferred from a row sample (post-filter on output values).
  const filterCols = React.useMemo(() => {
    if (qsource) return srcInfo ? [...srcInfo.columns.filter((c) => c.name !== 'id'), ...(srcInfo.lookups || [])] : [];
    if (filterable) return filterable.map((fc) => ({ name: fc.key, category: ['number', 'date', 'boolean'].includes(fc.type) ? fc.type : 'text' }));
    if (!data || !data.columns) return [];
    const rows = data.rows || [];
    return data.columns.map((name) => {
      const v = rows.map((r) => r[name]).find((x) => x != null);
      return { name, category: (typeof v === 'number') ? 'number' : 'text' };
    });
  }, [qsource, srcInfo, data, filterable]);
  const colCat = Object.fromEntries(filterCols.map((c) => [c.name, c.category]));
  // For filterable keys, value pickers come from the declared source/value
  // column; keys without one gracefully fall back to free text.
  const fcFetchValues = React.useMemo(() => {
    if (!filterable) return undefined;
    const map = Object.fromEntries(filterable.map((fc) => [fc.key, fc]));
    return (_src, col) => {
      const fc = map[col];
      return (fc && fc.source && fc.value_column)
        ? window.AtlasApi.views.values(fc.source, fc.value_column)
        : Promise.reject(new Error('no value picker'));
    };
  }, [filterable]);
  const applyFilters = () => { const fl = coerceFilters(filters, colCat); setApplied(fl); run(false, fl); };
  const clearFilters = () => { setFilters([]); setApplied([]); run(false, []); };

  // Columns offered by the export dialog: the spec's curated table_columns if
  // present, else the last run's result columns humanized. Server-side export
  // re-runs the query with the current ad-hoc filters, up to 10,000 rows.
  const exportCols = React.useMemo(() => {
    const tc = Array.isArray(spec.table_columns) && spec.table_columns.length ? spec.table_columns : null;
    if (tc) return tc.map((c) => ({ key: c.key, label: c.label || window.humanizeCol(c.key) }));
    return ((data && data.columns) || []).map((c) => ({ key: c, label: window.humanizeCol(c) }));
  }, [spec, data]);

  // Drill inherits the view's base scope PLUS the applied ad-hoc filters.
  const onSelect = (sel) => {
    const merged = qsource
      ? { ...spec, query: { ...spec.query, filters: [...((spec.query && spec.query.filters) || []), ...applied] } }
      : spec;
    setDrill({ spec: merged, data, selection: sel });
  };

  const Icon = window.Icon;
  const EIco = detail && Icon[ENTITY_ICON[detail.source_entity]];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <window.Btn size="sm" variant="ghost" icon={<Icon.ChevronL size={14} />} onClick={() => (back ? back() : window.AtlasNav.switchView('analytics'))}>Analytics</window.Btn>
        {EIco && <EIco size={16} color="var(--accent)" />}
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{detail ? detail.name : 'Loading…'}</span>
        {detail && detail.group_name && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-3)' }}>
            <Icon.Folder size={11} /> {detail.group_name}
          </span>
        )}
        {detail && <window.Pill hue={VIS_HUE[detail.visibility]} sz="sm">{(VIS_OPTS.find((o) => o[0] === detail.visibility) || [])[1]}</window.Pill>}
        {data && data.ran_at && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{window.relativeTime(data.ran_at)}{data.cached ? ' · cached' : ''}</span>}
        <div style={{ flex: 1 }} />
        {filterCols.length > 0 && (
          <window.Btn size="sm" variant={applied.length ? 'accent' : 'outline'} icon={<Icon.Filter size={12} />} onClick={() => setFilterOpen((v) => !v)}>
            {applied.length ? `Filters · ${applied.length}` : 'Filter'}
          </window.Btn>
        )}
        {detail && detail.can_edit && onEdit && <window.Btn size="sm" variant="outline" icon={<Icon.Edit size={12} />} onClick={() => onEdit(detail)}>Edit</window.Btn>}
        {/* Pin this view as a dashboard widget. Any viewer may try — the widget
            POST is gated server-side to dashboards they can edit. Only offered
            when the spec carries runnable SQL (widgets run spec.sql frozen). */}
        {detail && detail.spec && detail.spec.sql && window.SaveToDashboardDialog && (
          <window.Btn size="sm" variant="outline" icon={<Icon.Grid size={12} />} title="Pin this view as a widget on a dashboard"
            onClick={() => setPinOpen(true)}>Add to dashboard</window.Btn>
        )}
        {detail && (
          <window.Btn size="sm" variant="outline" icon={<Icon.Copy size={12} />} title="Duplicate into your private views"
            onClick={async () => {
              try {
                const d = await window.AtlasApi.views.duplicate(detail.id);
                window.pushToast({ type: 'success', title: 'View duplicated', body: d.name });
                if (window.AtlasNav && window.AtlasNav.goToView) window.AtlasNav.goToView(d.id);
              } catch (e) { window.pushToast({ type: 'error', title: "Couldn't duplicate", body: (e && e.message) || String(e) }); }
            }}>Duplicate</window.Btn>
        )}
        {detail && detail.can_edit && <window.Btn size="sm" variant="outline" icon={<Icon.Users size={12} />} onClick={() => setShareOpen(true)}>Share</window.Btn>}
        {detail && (
          <window.Btn size="sm" variant="outline" icon={<Icon.External size={12} />} disabled={!exportCols.length}
            onClick={() => setExportOpen(true)} title="Export as CSV — up to 10,000 rows">Export</window.Btn>
        )}
        <window.Btn size="sm" variant="ghost" icon={<Icon.Refresh size={12} />} onClick={() => run(true, applied)} title="Refresh" />
      </div>
      {filterOpen && filterCols.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {filters.map((f, i) => (
              <div key={i} style={{ width: 260 }}>
                <FilterRow f={f} source={qsource || null} cols={filterCols} fetchValues={fcFetchValues}
                  onChange={(patch) => setFilters(filters.map((x, j) => j === i ? { ...x, ...patch } : x))}
                  onRemove={() => setFilters(filters.filter((_, j) => j !== i))} />
              </div>
            ))}
            <button onClick={() => setFilters([...filters, { column: '', op: '=', value: '' }])}
              style={{ border: '1px dashed var(--border-strong)', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)', borderRadius: 6, padding: '6px 10px', alignSelf: 'flex-start' }}>+ Add filter</button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <window.Btn size="sm" variant="accent" onClick={applyFilters}>Apply</window.Btn>
            <window.Btn size="sm" variant="ghost" onClick={clearFilters} disabled={!filters.length && !applied.length}>Clear</window.Btn>
            {detail && detail.can_edit && (
              <window.Btn size="sm" variant="outline" title="Everyone who opens this view starts with these filters"
                onClick={async () => {
                  try {
                    const fl = coerceFilters(filters, colCat);
                    await window.AtlasApi.views.update(detail.id, { default_filters: fl });
                    setApplied(fl); run(false, fl);
                    window.pushToast({ type: 'success', title: fl.length ? 'Default filters saved' : 'Default filters cleared' });
                  } catch (e) { window.pushToast({ type: 'error', title: "Couldn't save defaults", body: (e && e.message) || String(e) }); }
                }}>Save as default</window.Btn>
            )}
          </div>
        </div>
      )}
      <div style={{ flex: 1, overflow: 'auto', padding: 16, minWidth: 0 }}>
        {detail && detail.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>{detail.description}</div>}
        {err && <div style={{ fontSize: 12, color: 'var(--rose-fg)', padding: 10 }}>{err}</div>}
        {detail && data ? (
          <window.WidgetRenderer spec={detail.spec} data={data} onSelect={onSelect} />
        ) : !err ? (
          <window.Skeleton w="100%" h={240} style={{ borderRadius: 8 }} />
        ) : null}
      </div>
      {shareOpen && detail && (
        <ShareViewDialog view={detail} onClose={() => setShareOpen(false)} onSaved={(u) => setDetail((d) => ({ ...d, ...u }))} />
      )}
      {exportOpen && detail && (
        <window.ExportDialog title={detail.name} columns={exportCols}
          onClose={() => setExportOpen(false)}
          onExport={(cols) => window.AtlasApi.views.exportCsv(detail.id, { filters: applied, columns: cols })} />
      )}
      {drill && window.DrillInspector && (
        <window.DrillInspector spec={drill.spec} data={drill.data} selection={drill.selection}
          onClose={() => setDrill(null)} api={window.AtlasApi.views} />
      )}
      {pinOpen && detail && window.SaveToDashboardDialog && (
        <window.SaveToDashboardDialog open onClose={() => setPinOpen(false)}
          spec={{ ...detail.spec, title: detail.spec.title || detail.name }}
          sourceRequest={null}
          onSaved={() => setPinOpen(false)} />
      )}
    </div>
  );
};


// ── Builder: create / edit a view via Manual or AI ────────────────────────────
const VE_CHART_TYPES = [
  ['table', 'Table'], ['bar', 'Bar'], ['horizontal_bar', 'Horizontal bar'], ['stacked_bar', 'Stacked bar'],
  ['line', 'Line'], ['area', 'Area'], ['pie', 'Pie'], ['donut', 'Donut'], ['kpi', 'Single stat'],
];
const VE_AGGS = [['count', 'Count'], ['count_distinct', 'Count distinct'], ['sum', 'Sum'], ['avg', 'Average'], ['min', 'Min'], ['max', 'Max']];
const VE_OPS = [['=', '='], ['!=', '≠'], ['>', '>'], ['>=', '≥'], ['<', '<'], ['<=', '≤'], ['between', 'between'], ['contains', 'contains'], ['starts_with', 'starts with'], ['in', 'any of'], ['not_in', 'none of'], ['is_null', 'is empty'], ['is_not_null', 'is not empty']];
const _hc = (s) => (window.humanizeCol ? window.humanizeCol(s) : String(s || ''));

// UI filter rows {column, op, value, value2} → the compiler's typed model
// (mirrors the analytics ManualBuilder's coercion).
const _coerceVal = (v, cat) => (cat === 'number' ? Number(v) : cat === 'boolean' ? (v === true || v === 'true') : v);
const coerceFilters = (filters, colCat) => filters.filter((f) => f.column && f.op).map((f) => {
  const cat = colCat[f.column] || 'text';
  if (f.op === 'is_null' || f.op === 'is_not_null') return { column: f.column, op: f.op, value: null };
  if (f.op === 'between') return { column: f.column, op: 'between', value: [_coerceVal(f.value, cat), _coerceVal(f.value2, cat)] };
  if (f.op === 'in' || f.op === 'not_in') return { column: f.column, op: f.op, value: Array.isArray(f.value) ? f.value : String(f.value || '').split(',').map((s) => s.trim()).filter(Boolean) };
  return { column: f.column, op: f.op, value: _coerceVal(f.value, cat) };
});
// Stored filters → editable UI rows (between splits into value/value2).
const filtersToRows = (filters) => (filters || []).map((f) => {
  if (f.op === 'between' && Array.isArray(f.value)) return { column: f.column, op: f.op, value: f.value[0] ?? '', value2: f.value[1] ?? '' };
  if (f.op === 'in' || f.op === 'not_in') return { column: f.column, op: f.op, value: Array.isArray(f.value) ? f.value : [] };
  return { column: f.column, op: f.op, value: f.value ?? '' };
});

// One filter row (column / op / typed value picker) — shared by the editor and
// the open-view filter bar. Value pickers hit the any-role /views/values.
const FilterRow = ({ f, cols, source, onChange, onRemove, fetchValues }) => {
  const colCat = Object.fromEntries((cols || []).map((c) => [c.name, c.category]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6, border: '1px solid var(--border)', borderRadius: 6, padding: 6 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <window.Combobox value={f.column} onChange={(e) => onChange({ column: e.target.value, value: '', value2: '' })} searchable
          options={[{ value: '', label: 'column…' }, ...(cols || []).filter((c) => c.name !== 'id').map((c) => ({ value: c.name, label: _hc(c.name) }))]} style={{ flex: 1 }} />
        <button onClick={onRemove} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)' }}>×</button>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <window.Combobox value={f.op} onChange={(e) => onChange({ op: e.target.value, value: ['in', 'not_in'].includes(e.target.value) ? [] : '', value2: '' })}
          options={VE_OPS.map(([v, l]) => ({ value: v, label: l }))} style={{ width: 120, flexShrink: 0 }} />
        {f.column && window.FilterValueInput && (
          <window.FilterValueInput source={source} column={f.column} cat={colCat[f.column] || 'text'} op={f.op}
            f={f} setF={(patch) => onChange(patch)} fetchValues={fetchValues || window.AtlasApi.views.values} />
        )}
      </div>
    </div>
  );
};

const Seg = ({ value, onChange, options }) => (
  <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
    {options.map(([v, label]) => (
      <button key={v} onClick={() => onChange(v)} style={{
        border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 500, padding: '4px 10px',
        background: value === v ? 'var(--accent-soft)' : 'transparent', color: value === v ? 'var(--accent-text)' : 'var(--text-3)',
      }}>{label}</button>
    ))}
  </div>
);
const FLbl = ({ children }) => <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-4)', marginBottom: 5 }}>{children}</div>;

const ViewEditor = ({ initial, prefill, initialGroup, onSaved, onCancel }) => {
  const editing = !!(initial && initial.id);
  const q0 = (initial && initial.spec && initial.spec.query) || {};
  // `prefill` seeds a NEW view from elsewhere (the drill drawer's "Open as
  // table"): {source, columns, filters, title} — a full-page record list of
  // the records behind a clicked chart element, ready to refine and save.
  const p0 = prefill || {};
  const [tab, setTab] = React.useState((editing && initial.spec && initial.spec.sql && !q0.source) ? 'ai' : 'manual');
  const [sources, setSources] = React.useState(null);
  const [source, setSource] = React.useState((initial && initial.source_entity) || q0.source || p0.source || 'companies');
  const [mode, setMode] = React.useState(q0.mode === 'aggregate' ? 'aggregate' : 'list');
  const [cols, setCols] = React.useState(q0.columns || q0.dimensions || p0.columns || []);
  const [measures, setMeasures] = React.useState((q0.measures && q0.measures.length) ? q0.measures.map((m) => ({ agg: m.agg, column: m.column || '' })) : [{ agg: 'count', column: '' }]);
  const [filters, setFilters] = React.useState(filtersToRows(q0.filters && q0.filters.length ? q0.filters : p0.filters));
  const [drill, setDrill] = React.useState(null);
  const [chartType, setChartType] = React.useState((initial && initial.spec && initial.spec.chart_type) || 'bar');
  const [sortBy, setSortBy] = React.useState((q0.sort && q0.sort.by) || '');
  const [sortDir, setSortDir] = React.useState((q0.sort && q0.sort.dir) || 'desc');
  const [limit, setLimit] = React.useState(q0.limit || 200);
  const [prompt, setPrompt] = React.useState('');
  const [spec, setSpec] = React.useState((editing && initial.spec) || null);
  const [result, setResult] = React.useState(null);
  const [name, setName] = React.useState((initial && initial.name) || p0.title || '');
  const [visibility, setVisibility] = React.useState((initial && initial.visibility) || 'private');
  const [group, setGroup] = React.useState((initial && initial.group_name) || initialGroup || '');
  const [groups, setGroups] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    window.AtlasApi.views.list()
      .then((v) => setGroups([...new Set((v || []).map((x) => x.group_name).filter(Boolean))].sort()))
      .catch(() => setGroups([]));
  }, []);

  React.useEffect(() => { window.AtlasApi.views.sources().then((s) => setSources(Array.isArray(s) ? s : [])).catch(() => setSources([])); }, []);
  React.useEffect(() => { if (editing && initial.id) window.AtlasApi.views.run(initial.id, false).then(setResult).catch(() => {}); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  // Prefill: once the catalog is in, keep only columns the source can actually
  // select (drill rows carry synthetic <x>_name link columns) and auto-run.
  React.useEffect(() => {
    if (!prefill || sources === null) return;
    const s = (sources || []).find((x) => x.name === source);
    const valid = new Set(s ? [...s.columns.map((c) => c.name), ...((s.lookups || []).map((l) => l.name))] : []);
    const keep = cols.filter((c) => valid.has(c) && c !== 'id');
    setCols(keep);
    if (keep.length) runManual({ ...buildModel(), mode: 'list', columns: keep, dimensions: [] });
  }, [sources]);  // eslint-disable-line react-hooks/exhaustive-deps

  const src = (sources || []).find((s) => s.name === source) || null;
  const realCols = src ? src.columns.map((c) => c.name).filter((c) => c !== 'id') : [];
  // Lookup (FK→name) columns work as list columns, group-bys AND filters now.
  const lookupCols = src ? (src.lookups || []).map((l) => l.name) : [];
  const colChoices = [...realCols, ...lookupCols].filter((c) => !cols.includes(c));
  const filterCols = src ? [...src.columns.filter((c) => c.name !== 'id'), ...(src.lookups || [])] : [];

  const buildModel = () => ({
    source, mode,
    columns: mode === 'list' ? cols : [],
    dimensions: mode === 'aggregate' ? cols : [],
    measures: mode === 'aggregate' ? measures.map((m) => (m.agg === 'count' && !m.column) ? { agg: 'count' } : { agg: m.agg, column: m.column || null }) : [],
    filters: coerceFilters(filters, Object.fromEntries(filterCols.map((c) => [c.name, c.category]))),
    sort: sortBy ? { by: sortBy, dir: sortDir } : null,
    limit: Number(limit) || 200,
    chart_type: mode === 'aggregate' ? chartType : 'table',
    title: name || undefined,
  });

  const runManual = async (model) => {
    setBusy(true); setErr('');
    try { const res = await window.AtlasApi.views.preview(model || buildModel()); setResult(res); setSpec(res.spec); }
    catch (e) { setErr((e && e.message) || String(e)); } finally { setBusy(false); }
  };
  const runAI = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setErr('');
    try {
      const res = await window.AtlasApi.views.generate({ request: prompt.trim(), ...(spec ? { base_spec: spec } : {}) });
      setResult(res); setSpec(res.spec);
      if (res.spec) { setSource(res.spec.query?.source || source); if (!name && res.spec.title) setName(res.spec.title); }
    } catch (e) { setErr((e && e.message) || String(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    if (!spec) { setErr('Run a preview first.'); return; }
    setBusy(true); setErr('');
    try {
      const srcEnt = spec.query?.source || source;
      const saved = editing
        ? await window.AtlasApi.views.update(initial.id, { name: (name || '').trim() || undefined, visibility, group_name: cleanFolder(group), spec })
        : await window.AtlasApi.views.create({ name: (name || '').trim() || spec.title || 'Untitled view', visibility, group_name: cleanFolder(group), source_entity: srcEnt, spec });
      window.pushToast({ type: 'success', title: editing ? 'View updated' : 'View saved', body: saved.name });
      if (onSaved) onSaved(saved);
    } catch (e) { setErr((e && e.message) || String(e)); window.pushToast({ type: 'error', title: "Couldn't save view", body: (e && e.message) || String(e) }); }
    finally { setBusy(false); }
  };

  const Icon = window.Icon;
  const railField = { marginBottom: 14 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <window.Btn size="sm" variant="ghost" icon={<Icon.ChevronL size={14} />} onClick={onCancel}>Analytics</window.Btn>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{editing ? 'Edit view' : 'New view'}</span>
        <Seg value={tab} onChange={setTab} options={[['manual', 'Manual'], ['ai', 'AI']]} />
        <div style={{ flex: 1 }} />
        <window.Btn size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />} onClick={save} disabled={busy || !spec}>{editing ? 'Update view' : 'Save view'}</window.Btn>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* config rail */}
        <div style={{ width: 330, flexShrink: 0, borderRight: '1px solid var(--border)', overflow: 'auto', padding: 14 }}>
          {tab === 'ai' ? (
            <div>
              <FLbl>Describe the view</FLbl>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') runAI(); }}
                placeholder="e.g. companies by industry with avg revenue, as a bar chart"
                style={{ width: '100%', fontSize: 12.5, border: '1px solid var(--border-strong)', borderRadius: 8, padding: 9, background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical' }} />
              <window.Btn size="sm" variant="accent" icon={<Icon.Sparkle size={12} />} disabled={busy || !prompt.trim()} onClick={runAI} style={{ marginTop: 8 }}>
                {busy ? 'Generating…' : (spec ? 'Refine' : 'Generate')}
              </window.Btn>
              <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 6 }}>⌘↵ to run.</div>
            </div>
          ) : (
            <div>
              <div style={railField}>
                <FLbl>Data source</FLbl>
                {sources === null ? <window.Skeleton w="100%" h={30} /> : (
                  <window.Combobox value={source} onChange={(e) => { setSource(e.target.value); setCols([]); setFilters([]); setSortBy(''); }}
                    searchable disabled={editing} options={(sources || []).map((s) => ({ value: s.name, label: s.name }))} />
                )}
              </div>
              <div style={railField}>
                <FLbl>View type</FLbl>
                <Seg value={mode} onChange={setMode} options={[['list', 'Records'], ['aggregate', 'Chart']]} />
              </div>
              <div style={railField}>
                <FLbl>{mode === 'list' ? 'Columns' : 'Group by'}</FLbl>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: cols.length ? 6 : 0 }}>
                  {cols.map((c) => (
                    <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, padding: '2px 6px 2px 9px', borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
                      {_hc(c)}<button onClick={() => setCols(cols.filter((x) => x !== c))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                </div>
                <window.Combobox value="" onChange={(e) => { if (e.target.value) setCols([...cols, e.target.value]); }} searchable
                  options={[{ value: '', label: '+ add column…' }, ...colChoices.map((c) => ({ value: c, label: _hc(c) }))]} />
              </div>
              {mode === 'aggregate' && (
                <>
                  <div style={railField}>
                    <FLbl>Measures</FLbl>
                    {measures.map((m, i) => (
                      <div key={i} style={{ display: 'flex', gap: 5, marginBottom: 5 }}>
                        <window.Combobox value={m.agg} onChange={(e) => setMeasures(measures.map((x, j) => j === i ? { ...x, agg: e.target.value } : x))}
                          options={VE_AGGS.map(([v, l]) => ({ value: v, label: l }))} style={{ width: 120, flexShrink: 0 }} />
                        {m.agg !== 'count' && (
                          <window.Combobox value={m.column} onChange={(e) => setMeasures(measures.map((x, j) => j === i ? { ...x, column: e.target.value } : x))} searchable
                            options={[{ value: '', label: 'column…' }, ...realCols.map((c) => ({ value: c, label: _hc(c) }))]} style={{ flex: 1 }} />
                        )}
                        {measures.length > 1 && <button onClick={() => setMeasures(measures.filter((_, j) => j !== i))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)' }}>×</button>}
                      </div>
                    ))}
                    <button onClick={() => setMeasures([...measures, { agg: 'count', column: '' }])} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)' }}>+ Add measure</button>
                  </div>
                  <div style={railField}>
                    <FLbl>Chart type</FLbl>
                    <window.Combobox value={chartType} onChange={(e) => setChartType(e.target.value)} options={VE_CHART_TYPES.map(([v, l]) => ({ value: v, label: l }))} />
                  </div>
                </>
              )}
              <div style={railField}>
                <FLbl>Filters</FLbl>
                {filters.map((f, i) => (
                  <FilterRow key={i} f={f} source={source} cols={filterCols}
                    onChange={(patch) => setFilters(filters.map((x, j) => j === i ? { ...x, ...patch } : x))}
                    onRemove={() => setFilters(filters.filter((_, j) => j !== i))} />
                ))}
                <button onClick={() => setFilters([...filters, { column: '', op: '=', value: '' }])} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)' }}>+ Add filter</button>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', ...railField }}>
                <div style={{ flex: 1 }}>
                  <FLbl>Sort by</FLbl>
                  <window.Combobox value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    options={[{ value: '', label: 'Default' }, ...cols.map((c) => ({ value: c, label: _hc(c) }))]} />
                </div>
                <window.Btn size="sm" variant="outline" onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')} title="Direction">{sortDir === 'desc' ? '↓' : '↑'}</window.Btn>
                <div style={{ width: 64 }}>
                  <FLbl>Limit</FLbl>
                  <window.TextInput value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^\d]/g, ''))} />
                </div>
              </div>
              <window.Btn variant="accent" disabled={busy || !source || !cols.length} onClick={() => runManual()} icon={<Icon.Play size={12} />}>
                {busy ? 'Running…' : 'Run preview'}
              </window.Btn>
            </div>
          )}
        </div>

        {/* preview */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <window.TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="View name" style={{ flex: 1, minWidth: 180, fontSize: 14, fontWeight: 600 }} />
            <FolderPicker value={group} onChange={setGroup} groups={groups} />
            <VisInline value={visibility} onChange={setVisibility} />
          </div>
          {err && <div style={{ background: 'var(--rose-bg)', color: 'var(--rose-fg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, marginBottom: 12 }}>{err}</div>}
          {spec && result ? (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 12, minHeight: 220 }}>
              <window.WidgetRenderer spec={spec} data={result}
                onSelect={(sel) => setDrill({ spec, data: result, selection: sel })} />
              <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--text-4)', textAlign: 'center' }}>Tip: click any point to see the data behind it.</div>
            </div>
          ) : (
            <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 12, padding: '50px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12.5 }}>
              {tab === 'ai' ? 'Describe the view you want, then Generate.' : 'Pick columns and Run preview.'}
            </div>
          )}
        </div>
      </div>
      {drill && window.DrillInspector && (
        <window.DrillInspector spec={drill.spec} data={drill.data} selection={drill.selection}
          onClose={() => setDrill(null)} api={window.AtlasApi.views} />
      )}
    </div>
  );
};

// Compact inline visibility picker for the editor's preview bar.
const VisInline = ({ value, onChange }) => (
  <Seg value={value} onChange={onChange} options={VIS_OPTS.map(([v, label]) => [v, label])} />
);

const ViewsView = ({ viewId, goToView, back, activeUser, builderPrefill, onPrefillConsumed }) => {
  const [editor, setEditor] = React.useState(null);   // null | {} (new) | view (edit) | {prefill}
  // A prefill (from the drill drawer's "Open as table") opens the editor
  // pre-loaded with the drill's source + filters; consume it so it fires once.
  React.useEffect(() => {
    if (builderPrefill) {
      setEditor({ prefill: builderPrefill });
      if (onPrefillConsumed) onPrefillConsumed();
    }
  }, [builderPrefill]);  // eslint-disable-line react-hooks/exhaustive-deps
  const go = goToView || ((id) => window.AtlasNav && window.AtlasNav.goToView && window.AtlasNav.goToView(id));
  if (editor) {
    return <ViewEditor initial={editor.id ? editor : null} prefill={editor.prefill || null}
      onCancel={() => setEditor(null)}
      onSaved={(v) => { setEditor(null); if (v && v.id) go(v.id); }} />;
  }
  if (viewId) return <SingleView viewId={viewId} back={back} activeUser={activeUser} onEdit={(v) => setEditor(v)} />;
  // No viewId and no editor: the views gallery moved into the Analytics
  // library (PR #151) — app.jsx only mounts ViewsView with a viewId, so this
  // branch is unreachable in practice.
  return null;
};

// FilterRow + the row↔wire converters are also used by the dashboard widget
// filter popover in analytics.jsx (esbuild modules are isolated — cross-file
// symbols go through window.*).
Object.assign(window, { ViewsView, SaveViewDialog, ShareViewDialog, ViewEditor, FilterRow, coerceFilters, filtersToRows });
